import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import type { DurationStats as DurationStatsType } from '../../types';

interface DurationStatsProps {
  stats: DurationStatsType;
  title?: string;
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

export function DurationStats({ stats, title = 'Duration Analysis' }: DurationStatsProps) {
  return (
    <div className="bg-white rounded-lg border border-surface-200 p-3 shadow-card h-full flex flex-col">
      <div className="flex items-center gap-2 mb-2 flex-shrink-0">
        <div className="w-7 h-7 rounded bg-emerald-100 flex items-center justify-center">
          <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h2 className="text-sm font-semibold text-surface-800">{title}</h2>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-2 mb-2 flex-shrink-0">
        <div className="p-2 bg-surface-50 rounded border border-surface-100">
          <p className="text-[10px] font-medium text-surface-500 uppercase tracking-wide">Average</p>
          <p className="text-sm font-bold text-surface-800">{formatDuration(stats.average)}</p>
        </div>
        <div className="p-2 bg-surface-50 rounded border border-surface-100">
          <p className="text-[10px] font-medium text-surface-500 uppercase tracking-wide">Median</p>
          <p className="text-sm font-bold text-surface-800">{formatDuration(stats.median)}</p>
        </div>
        <div className="p-2 bg-surface-50 rounded border border-surface-100">
          <p className="text-[10px] font-medium text-surface-500 uppercase tracking-wide">Maximum</p>
          <p className="text-sm font-bold text-danger-dark">{formatDuration(stats.max)}</p>
        </div>
      </div>

      {/* Histogram */}
      {stats.histogram.length > 0 ? (
        <div className="flex-1 min-h-0">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={stats.histogram} margin={{ top: 5, right: 5, left: -10, bottom: 25 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis
                dataKey="range"
                tick={{ fontSize: 9, fill: '#64748b' }}
                angle={-45}
                textAnchor="end"
                interval={0}
                height={40}
              />
              <YAxis tick={{ fontSize: 9, fill: '#64748b' }} allowDecimals={false} width={30} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#fff',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                }}
                formatter={(value: number) => [`${value} scans`, 'Count']}
              />
              <Bar dataKey="count" fill="#6D9E51" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="flex-1 min-h-0 flex items-center justify-center text-surface-400">
          No duration data available
        </div>
      )}
    </div>
  );
}
